<template>
  <v-menu
    offset-y
    left
    nudge-bottom="20"
    :elevation="$vuetify.theme.dark ? 9 : 8"
    content-class="list-style notification-menu-content"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-badge
        :color="notifications.newNotifications ? 'info' : 'transparent'"
        offset-x="11"
        offset-y="13"
        overlap
        :content="notifications.newNotifications"
      >
        <v-icon v-bind="attrs" size="22" :color="light ? 'secondary' : 'white'" v-on="on">
          {{ icons.mdiBellOutline }}
        </v-icon>
      </v-badge>
    </template>
    <v-card class="app-bar-notification-content-container">
      <perfect-scrollbar class="ps-user-notifications" :options="perfectScrollbarOptions">
        <v-list class="py-0">
          <!-- Header -->
          <v-list-item class="d-flex" link>
            <div class="d-flex align-center justify-space-between flex-grow-1">
              <span class="font-weight-semibold">Notifications</span>
              <v-chip
                v-if="notifications.newNotifications"
                class="v-chip-light-bg primary--text font-weight-semibold"
                small
              >
                {{ notifications.newNotifications }} New
              </v-chip>
            </div>
          </v-list-item>
          <v-divider></v-divider>

          <!-- Notifications -->
          <template v-for="(notification, index) in notificationArray">
            <v-list-item
              :key="notification.uuid"
              link
              :style="{ background: !notification.isviewed ? '#eee' : '#fff' }"
              @click="handleNotificationClick(notification)"
            >
              <!-- Avatar -->
              <v-list-item-avatar class="v-avatar-light-bg primary--text justify-center" size="40">
                <span class="text-sm">{{
                  getInitialName(notification.data.name ? notification.data.name : 'M')
                }}</span>
              </v-list-item-avatar>

              <!-- Content -->
              <v-list-item-content class="d-block">
                <v-list-item-title class="text-sm font-weight-semibold">
                  {{ notification.data.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-sm">
                  {{ notification.data.message }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <!-- Item Action/Time -->
              <v-list-item-action>
                <span class="text--secondary text-xs">{{
                  formatDateToMonthShort(notification.createdAt)
                }}</span>
              </v-list-item-action>
            </v-list-item>
            <v-divider :key="index"></v-divider>
          </template>
          <v-list-item key="read-all-btn" class="read-all-btn-list-item">
            <v-btn
              block
              color="primary"
              @click="$router.push({ name: 'user-profile', query: { notification: 'true' } })"
            >
              Read All Notifications
            </v-btn>
          </v-list-item>
        </v-list>
      </perfect-scrollbar>
    </v-card>
  </v-menu>
</template>

<script>
import { mdiBellOutline } from '@mdi/js'
import { getInitialName, useRouter } from '@core/utils'
import { ref, onUnmounted } from '@vue/composition-api'

import store from '@/store'
import { storeActions, storeMutations } from '@/store/storeConstants'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import { formatDateToMonthShort } from '@core/utils/filter'
import useTabService from '@/@core/comp-functions/useTabService'
import Toaster from '@core/utils/sweetToast'
import { exportDataToExcel } from '@core/utils/csvService'
import axios from 'axios'

export default {
  components: {
    // 3rd Party
    PerfectScrollbar,
  },
  props: {
    light: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { router } = useRouter()
    const { estimationTab, gotoCrmTab } = useTabService()
    const page = ref(1)
    const count = ref(0)
    const notificationArray = ref([])
    const notifications = ref({})
    const userNotification = ref([])
    const lastNotificationId = ref(undefined)

    const perfectScrollbarOptions = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const downloadReport = data => {
      const baseUrl = `${process.env.VUE_APP_ROOT_API}/reportservice/download/${data.file_type}/${data.uuid}`
      axios
        .get(
          baseUrl,
          { responseType: 'blob' }, // !!!
        )
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `${data.name}.${data.file_type}`)
          document.body.appendChild(link)
          link.click()
        })
        .catch(() => {
          // do nothing
        })
    }

    // To call api only on tab active
    let isTabActive = !document.hidden
    document.addEventListener('visibilitychange', () => {
      isTabActive = !document.hidden
    })

    const loadNotifications = () => {
      if (!isTabActive) return
      page.value = 1
      const payload = {
        component: '/notification/',
        filter: {
          limit: 10,
          offset: 0,
          last_notification: lastNotificationId.value,
        },
      }
      store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload).then(({ data }) => {
        if (data.success && data.data) {
          // if policyUpdated key is coming than show notification
          if (data.data.policyUpdated) {
            Toaster.success('Your policy has been updated please login again')
          }

          if (lastNotificationId.value) {
            notificationArray.value.unshift(...data.data.rows)
            if (notificationArray.value.length > 10) {
              notificationArray.value.splice(10)
            }
          } else {
            notificationArray.value = data.data.rows
          }

          if (notificationArray.value.length) {
            lastNotificationId.value = notificationArray.value[0].uuid
          }
          notifications.value = data.data

          if (data.meta !== undefined) {
            count.value = data.meta.count
            // for estimation ocr functionality
            if (data.meta.userNotification && data.meta.userNotification.length) {
              data.meta.userNotification.forEach(element => {
                const userNotify = JSON.parse(element)
                if (userNotify?.code === 200) {
                  window.open(userNotify.signedUrl, '_blank')
                }
                if (userNotify?.code === 5000) {
                  downloadReport(userNotify)
                }
                if (userNotify?.code === 2001) {
                  store.commit(`estimation/${storeMutations.SET_OCR_STATUS}`, false)
                }
                Toaster.success(userNotify?.message, 'success')
              })
            }
          }
        }
      })
    }

    const myInterval = setInterval(() => {
      loadNotifications()
    }, 50000)

    loadNotifications()
    const updateNotification = uuid => {
      const payload = {
        component: `/notification/?uuid=${uuid}`,
      }

      store.dispatch(`common/${storeActions.UPDATE}`, payload).then(({ data }) => {
        if (data.success) {
          loadNotifications()
        }
      })
    }

    const getRouteView = module => {
      if (module === 'accounts') return 'account-view'
      if (module === 'opportunities') return 'opportunity-view'
      if (module === 'contacts') return 'contact-view'

      return 'lead-view'
    }

    const findRelatedModule = propType => {
      // CRM Props
      if (['accounts', 'opportunities', 'contacts', 'leads'].includes(propType)) {
        return 'CRM'
      }
      // PROJECT Props
      if (
        [
          'RFITasks',
          'punchlistTasks',
          'changeOrdersTasks',
          'masterChangeOrdersTasks',
          'submittalTasks',
          'submittalTasks',
        ].includes(propType)
      ) {
        return 'PROJECT'
      }
      // INVENTORY Props
      if (
        [
          'warehouse',
          'inventory',
          'purchaseOrders',
          'changeOrder',
          'receipt',
          'delivery',
          'shopPull',
          'shopPullRequisition',
          'requisitionWithoutIssue',
          'requisition',
          'issueAgainstRequisition',
          'internalTransfer',
          'signOut',
          'bringBack',
          'inventoryAdjustment',
          'physicalCount',
        ].includes(propType)
      ) {
        return 'INVENTORY'
      }
      // Vendor Props
      if (['VENDOR', 'vendor'].includes(propType)) {
        return 'VENDOR'
      }

      return false
    }

    const getCRMRouteView = notificationItem => {
       const queryParam =
        notificationItem.module === 'comment'
          ? { chatter: 'true', chatterId: notificationItem.uuid }
          : {};

      let routeName = 'account-view'

      if (notificationItem.prop_type === 'accounts') routeName = 'account-view'
      if (notificationItem.prop_type === 'opportunities') routeName = 'opportunity-view'
      if (notificationItem.prop_type === 'contacts') routeName = 'contact-view'
      if (notificationItem.prop_type === 'lead') routeName = 'lead-view'

      const payload = {
        paramName: 'uuid',
        name: routeName,
        uuid: notificationItem.prop_id,
      }
      gotoCrmTab(payload, routeName)
      router
        .push({ name: routeName, params: { uuid: notificationItem.prop_id }, query: queryParam })
        .catch(() => {})
    }

    const getProjectRouteView = notificationItem => {
      const queryParam =
        notificationItem.module === 'comment'
          ? { chatter: 'true', chatterId: notificationItem.uuid }
          : {};

      switch (notificationItem.prop_type) {
        case 'RFITasks':
          router.push({
            name: 'rfi-edit',
            params: {
              projectId: notificationItem.extra_data.projectId,
              rfiId: notificationItem.prop_id,
            },
            query: queryParam,
          })
          break
        case 'punchlistTasks':
          router.push({
            name: 'punchlist-edit',
            params: {
              projectId: notificationItem.extra_data.projectId,
              punchlistId: notificationItem.prop_id,
            },
            query: queryParam,
          })
          break
        case 'changeOrdersTasks':
          router.push({
            name: 'change-order-edit',
            params: {
              projectId: notificationItem.extra_data.projectId,
              changeOrderId: notificationItem.prop_id,
            },
            query: queryParam,
          })
          break
        case 'masterChangeOrdersTasks':
          router.push({
            name: 'master-change-order-edit',
            params: {
              projectId: notificationItem.extra_data.projectId,
              masterId: notificationItem.prop_id,
            },
            query: queryParam,
          })
          break
        case 'submittalTasks':
          router.push({
            name: 'submittal-edit',
            params: {
              projectId: notificationItem.extra_data.projectId,
              submittalId: notificationItem.prop_id,
            },
            query: queryParam,
          })
          break
        case 'projects':
          router.push({
            name: 'project-my-todo',
            params: {
              projectId: notificationItem.extra_data.projectId,
            },
            query: queryParam,
          })
          break

        default:
          router.push({
            name: 'project-dashboard',
            params: { projectId: notificationItem.extra_data.projectId },
          })
          break
      }
    }

    const getInventoryRouteView = notificationItem => {
       const queryParam =
        notificationItem.module === 'comment'
          ? { chatter: 'true', chatterId: notificationItem.uuid }
          : {};
      let routeName = 'inventory-list'

      if (notificationItem.prop_type === 'warehouse') {
        routeName = 'warehouse-details'
      }
      if (notificationItem.prop_type === 'inventory') {
        routeName = 'view-inventory'
      }
      if (notificationItem.prop_type === 'purchaseOrders') {
        routeName = 'view-po'
      }
      if (notificationItem.prop_type === 'changeOrder') {
        routeName = 'inv-view-co'
      }
      if (notificationItem.prop_type === 'receipt') {
        routeName = 'view-receipt'
      }
      if (notificationItem.prop_type === 'delivery') {
        routeName = 'view-delivery'
      }
      if (notificationItem.prop_type === 'shopPull') {
        routeName = 'view-shop-pull'
      }
      if (notificationItem.prop_type === 'shopPullRequisition') {
        routeName = 'view-shop-requisition'
      }
      if (notificationItem.prop_type === 'requisitionWithoutIssue') {
        routeName = 'view-requisition'
      }
      if (notificationItem.prop_type === 'requisition') {
        routeName = 'view-without-requisition'
      }
      if (notificationItem.prop_type === 'issueAgainstRequisition') {
        routeName = 'view-requisition-issue'
      }
      if (notificationItem.prop_type === 'internalTransfer') {
        routeName = 'view-internal-transfer'
      }
      if (notificationItem.prop_type === 'signOut') {
        routeName = 'view-signout'
      }
      if (notificationItem.prop_type === 'bringBack') {
        routeName = 'view-bring-back'
      }
      if (notificationItem.prop_type === 'inventoryAdjustment') {
        routeName = 'view-inventory-adjustment'
      }
      if (notificationItem.prop_type === 'physicalCount') {
        routeName = 'view-physical-count'
      }

      router
        .push({ name: routeName, params: { uuid: notificationItem.prop_id }, query: queryParam })
        .catch(() => {})
    }

    const getVendorRouteView = notificationItem => {
      const queryParam =
         notificationItem.module === 'comment'
           ? { chatter: 'true', chatterId: notificationItem.uuid }
           : {}
      let routeName = 'vendors'

      if (notificationItem.prop_type === 'vendors') {
        routeName = 'vendor-view'
      }

      router
        .push({ name: routeName, params: { uuid: notificationItem.prop_id }, query: queryParam })
        .catch(() => {})
    }

    const handleNotificationClick = notification => {
      if (notification.isviewed === 0) {
        updateNotification(notification.uuid)
      }

      const { data } = notification
      if (!data) return
      if (data.errorList) {
        const excelData = data.errorList.map(opt => {
          const { serialNo, tenant_id, createdAt, updatedAt, error, ...restData } = opt

          return {
            ...restData,
            error: error.join(', '),
          }
        })
        exportDataToExcel(excelData, `${data.name}.xlsx`)
        Toaster.success('Item list downloaded successfully.', 'success')
      } else if (data.module === 'CRM' || findRelatedModule(data.prop_type) === 'CRM') {
        getCRMRouteView(data)
      } else if (data.module === 'PROJECT' || findRelatedModule(data.prop_type) === 'PROJECT') {
        getProjectRouteView(data)
      } else if (data.module === 'inventory' || findRelatedModule(data.prop_type) === 'INVENTORY') {
        getInventoryRouteView(data)
      } else if (data.module === 'VENDOR' || findRelatedModule(data.prop_type) === 'VENDOR') {
        getVendorRouteView(data)
      } else if (data.module === 'estimationPdf') {
        window.open(data.signedUrl, '_blank')
      } else if (data.module === 'estimation') {
        const payload = {
          paramName: 'uuid',
          uuid: data.uuid,
          name: data.name,
        }
        estimationTab(payload, 'estimation-view')
        router.push({ name: 'estimation-view', params: { uuid: data.uuid } }).catch(() => {})
      } else if (data.module === 'estimation-comment') {
        if (data.page_id && data.pdf_id) {
          router
            .push({
              name: 'estimator-pdf',
              params: {
                uuid: data.uuid,
                pdfId: data.pdf_id,
                pageId: data.page_id,
              },
            })
            .catch(() => {})
        } else {
          router
            .push({
              name: 'estimation-tool-overview',
              params: {
                uuid: data.uuid,
              },
            })
            .catch(() => {})
        }
      } else if (data.module === 'field management' || data.prop_type === 'fieldProject') {
        router
          .push({
            name: 'field-visualizer-task',
            params: {
              project_id: data.prop_id,
              pdfId: data.extra_data.pdfId,
              pageId: data.extra_data.pageId,
              taskId: data.uuid,
            },
          })
          .catch(error => {
            const message = error?.message || ''
            if (message.includes('Avoided redundant navigation')) {
              Toaster.success('You are already on this page.', 'info')
            } else if (message.includes('Cannot navigate')) {
              Toaster.success('Navigation failed. Please check if the page exists.', 'danger')
            } else {
              Toaster.success('An unexpected error occurred while navigating.', 'danger')
            }
          })
      } else if (data.module === 'task-management') {
        router.push({
          name: 'all-task',
          params: {
            module: data.prop_type,
            moduleId: data.prop_id,
          },
          query: { task: data.uuid, dialog: true },
        })
      } else if (data.module === 'report') {
        store.commit('notification/setReportData', data)
        router
          .push({
            name: 'report-download',
            params: { uuid: notification.uuid },
          })
          .catch(() => {})
      } else if (data.module === 'comment' && data.prop_type === 'space_items') {
        router.push({
          name: 'table-task',
          params: {
            module: data.prop_type,
            moduleId: data.prop_id,
            spaceId: data.prop_id,
            taskId: data.uuid,
          },
          query: { task: data.prop_id, dialog: true },
        })
      } else if (data.module === 'comment') {
        const queryParam = { chatter: 'true', chatterId: data.uuid }
        const payload = {
          paramName: 'uuid',
          name: data.name,
          uuid: data.prop_id,
        }
        gotoCrmTab(payload, getRouteView(data.prop_type))
        router
          .push({
            name: getRouteView(data.prop_type),
            params: { uuid: data.prop_id },
            query: queryParam,
          })
          .catch(() => {})
      }
    }

    onUnmounted(() => {
      clearTimeout(myInterval)
    })

    return {
      notifications,
      getInitialName,
      userNotification,
      perfectScrollbarOptions,
      notificationArray,
      handleNotificationClick,
      formatDateToMonthShort,
      icons: {
        mdiBellOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.app-bar-notification-content-container {
  .read-all-btn-list-item {
    padding-top: 14px;
    padding-bottom: 14px;
    min-height: unset;
  }
}

.ps-user-notifications {
  max-height: calc(var(--vh, 1vh) * 80);
}

.notification-menu-content {
  @media #{map-get($display-breakpoints, 'xs-only')} {
    min-width: calc(100vw - (1.5rem * 2)) !important;
    left: 50% !important;
    transform: translateX(-50%);
  }
}
</style>
